import React from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { connect } from 'react-redux';
import { getDataFollowKeyByDot } from '../../helpers/Common';
import { COMPONENT_RESIZE } from '../GoldenLayoutWrapper';
import Loading from '../loading/Loading';
import NoData from './NoData';
import TableBody from './TableBody';
import TableHead from './TableHead';
import './table.scss';
import ConstCommon from './../Const';
import ScrollComponent from './../ScrollComponent';
import { sort, sortGroup } from './helpers';
import SizeTracker from './../../common/size-tracker/SizeTracker';

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const RESIZE_WAIT = 30;

class Table extends React.Component {
  constructor(props) {
    super(props);
    const { hideColumns, groups } = this.props;
    this.state = {
      schema: this.getSchema(hideColumns),
      hideColumns,
      sortState: props.sortState,
      countSort: Object.keys(props.sortState).length,
      ids: this.getIdsFromProps(this.props, props.sortState),
      groups,
      widths: {},
    };
    this.listRef = {};
    this.tableThead = React.createRef();
    this.debounceComponentResize = debounce(
      this.onComponentResize,
      RESIZE_WAIT,
    );
    this.isHidden = null;
  }

  componentDidMount() {
    this._isMounted = true;
    this.observerShow();
    const { ids, groups } = this.props;
    if (ids.length || (groups && groups.length)) {
      this.calcWidthFromTableHead();
    }
    window.addEventListener(COMPONENT_RESIZE, this.debounceComponentResize);
    this.container = this.table.closest('.lm_content');
  }

  componentWillReceiveProps(nextProps) {
    const { hideColumns, ids, schema, groups, sortState } = this.props;
    if (ids !== nextProps.ids) {
      this.setState({
        ids: this.getIdsFromProps(nextProps),
      });
    }

    if (groups !== nextProps.groups) {
      this.setState({
        groups: this.getGroupsFromNextProps(nextProps),
      });
    }

    if (hideColumns !== nextProps.hideColumns) {
      this.setState({
        hideColumns: nextProps.hideColumns,
        schema: this.getSchema(nextProps.hideColumns),
      });
    }

    if (schema !== nextProps.schema) {
      this.setState({
        schema: this.getSchema(nextProps.hideColumns, nextProps),
      });
    }

    if (sortState !== nextProps.sortState) {
      this.setState({
        sortState: nextProps.sortState,
      });
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { hideColumns } = this.state;
    const { schema, ids, groups, idHover, fontSize } = this.props;
    const isColumnChange = schema.length !== prevProps.schema.length;
    const isHideColumnChange = hideColumns !== prevState.hideColumns;
    const hasNewData = ids.length && !prevProps.ids.length;
    const hasNewGroup = groups.length && !prevProps.groups.length;
    const isChangeFontSize = fontSize !== prevProps.fontSize;
    if (
      isHideColumnChange ||
      isColumnChange ||
      hasNewData ||
      hasNewGroup ||
      isChangeFontSize
    ) {
      if (this.calcWidthFromTableHead) {
        this.calcWidthFromTableHead(true);
      }
    }
    if (idHover && idHover !== prevProps.idHover) {
      this.scrollView(idHover);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener(COMPONENT_RESIZE, this.debounceComponentResize);
  }

  observerShow = () => {
    this.observer = new MutationObserver(this.onGlContainerChange);

    this.lmItemContainer = this.table.closest('.lm_item_container');
    this.observer.observe(this.lmItemContainer, {
      attributes: true,
    });
  };

  onGlContainerChange = () => {
    const isHidden = this.lmItemContainer.style.display === 'none';
    if (isHidden !== this.isHidden) {
      this.isHidden = isHidden;
      if (isHidden === false && this.calcWidthFromTableHead) {
        this.calcWidthFromTableHead(true);
      }
    }
  };

  onComponentResize = () => {
    this.calcWidthFromTableHead(true);
  };

  setCalcWidth = func => {
    this.calcWidthFromTableHead = func;
  };

  setWidths = widths => {
    if (this._isMounted) {
      this.setState({
        widths,
      });
    }
  };

  getValueFromItemFunc = () => {
    let sortState = this.props.sortState;
    if (this.state && this.state.sortState) {
      sortState = this.state.sortState;
    }
    const { schema } = this.props;
    const [key] = Object.keys(sortState);
    const column = schema.find(item => item.key === key);
    if (
      column &&
      column.valueFromItem &&
      typeof column.valueFromItem === 'function'
    ) {
      return column.valueFromItem;
    }

    return item => getDataFollowKeyByDot(item, key);
  };

  getIdsFromProps = props => {
    let sortState = this.props.sortState;
    if (this.state && this.state.sortState) {
      sortState = this.state.sortState;
    }
    if (!Object.keys(sortState).length) {
      return props.ids;
    }
    const [type] = Object.values(sortState);

    return sort({
      type,
      ids: props.ids,
      data: props.data,
      valueFromItem: this.getValueFromItemFunc(),
    });
  };

  getGroupsFromNextProps = nextProps => {
    const { sortState } = this.state;
    if (!Object.keys(sortState).length) {
      return nextProps.groups;
    }
    const [type] = Object.values(sortState);

    return sortGroup({
      type,
      groups: nextProps.groups,
      data: nextProps.data,
      valueFromItem: this.getValueFromItemFunc(),
    });
  };

  getTheadHeight = () => {
    return this.tableThead.current ? this.tableThead.current.clientHeight : 0;
  };

  scrollView = idHover => {
    const { scrollRef } = this.props;

    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTop(
        this.listRef[idHover].offsetTop - this.getTheadHeight(),
      );
    }
  };

  reorderColumn = (startIndex, targetIndex) => {
    const { schema } = this.state;
    this.setState({
      schema: reorder(schema, startIndex, targetIndex),
    });
  };

  reorderRow = (startIndex, targetIndex) => {
    const { ids } = this.state;
    const { onRowReorder, onSort } = this.props;
    const newIds = reorder(ids, startIndex, targetIndex);
    this.setState({
      ids: newIds,
    });
    onSort({});
    onRowReorder(newIds);
  };

  getSchema = (hideColumns, props) => {
    let { schema } = this.props;
    if (props) {
      schema = props.schema;
    }
    return schema.filter(col => !hideColumns.includes(col.key));
  };

  getClassName = () => {
    const { className, multiHead, stickyHead, stickyFirstColumn } = this.props;

    let classNameTable =
      'custom-table scroll-table table-striped border-table w-100';
    if (multiHead) {
      classNameTable = [classNameTable, 'text-center table-hose'].join(' ');
    } else {
      classNameTable = [classNameTable, 'table-wl-summary'].join(' ');
    }

    if (className) {
      classNameTable = className;
    }

    if (stickyHead) {
      classNameTable += ' table-sticky-head';
    }

    if (stickyFirstColumn) {
      classNameTable += ' table-sticky-first-column';
    }

    return classNameTable;
  };

  sort = (newIds, sortState, isNew = false) => {
    const { groups, countSort } = this.state;
    const { onSort } = this.props;
    let newCountSort = isNew ? 1 : countSort + 1;
    let newSortState = { ...sortState };

    if (newCountSort === 3) {
      newSortState = {};
    }
    const isReset = !Object.keys(newSortState).length;
    if (groups) {
      this.setState({
        groups: isReset ? this.props.groups : newIds,
        sortState: newSortState,
        countSort: newCountSort,
      });
      return;
    }

    this.setState({
      ids: isReset ? this.props.ids : newIds,
      sortState: newSortState,
      countSort: newCountSort,
    });
    onSort(newSortState);
  };

  renderBody = () => {
    const { schema, ids, groups, widths } = this.state;
    const {
      clickRow,
      rowDraggable,
      getDataFromRedux,
      isLoading,
      isTrHover,
      idHover,
      isHighlight,
      noDataText,
      stickyFirstColumn,
      tdPadding,
      onToggleRow,
    } = this.props;
    const isNoData = !ids.length || (groups && !groups.length);
    if (
      (!isLoading && isNoData && !groups) ||
      (!isLoading && groups && !groups.length)
    ) {
      return <NoData noDataText={noDataText} schema={schema} />;
    }

    return (
      <TableBody
        tdPadding={tdPadding}
        listRef={this.listRef}
        getDataFromRedux={getDataFromRedux}
        draggable={rowDraggable}
        reorder={this.reorderRow}
        schema={schema}
        ids={ids}
        onToggleRow={onToggleRow}
        widths={widths}
        groups={groups}
        clickRow={clickRow}
        isTrHover={isTrHover}
        idHover={idHover}
        isHighlight={isHighlight}
        stickyFirstColumn={stickyFirstColumn}
      />
    );
  };

  onScroll = e => {
    const { onScrollFunction } = this.props;
    onScrollFunction(e);

    if (this.mouseEnter) {
      this.showScroll();
    }
  };

  clearAddHover = () => {
    const { changeIdHover } = this.props;

    changeIdHover('');
  };

  renderContentScroll = () => {
    const { ids, schema, widths, tableWidth, groups, sortState } = this.state;
    const {
      columnDraggable,
      getDataFromRedux,
      stickyFirstColumn,
      isLoading,
      hideColumns,
      thValign,
      resizeable,
      isShowHeadLoading,
    } = this.props;

    return (
      <div
        className={isLoading ? 'table-loading-blur' : ''}
        style={{ position: 'relative' }}
      >
        <table
          onMouseMove={this.clearAddHover}
          ref={el => (this.table = el)}
          className={this.getClassName()}
        >
          {(ids.length === 0 && isShowHeadLoading) || (
            <TableHead
              setParentFunction={this.setCalcWidth}
              sortState={sortState}
              groups={groups}
              tableThead={this.tableThead}
              thValign={thValign}
              hideColumns={hideColumns}
              widths={widths}
              resizeable={resizeable}
              tableWidth={tableWidth}
              stickyFirstColumn={stickyFirstColumn}
              ids={ids}
              draggable={columnDraggable}
              schema={schema}
              setWidths={this.setWidths}
              reorder={this.reorderColumn}
              onSort={this.sort}
              getDataFromRedux={getDataFromRedux}
            />
          )}
          {this.renderBody()}
        </table>
      </div>
    );
  };

  renderScroll = () => {
    const { onScrollFunction, scrollRef } = this.props;

    return (
      <SizeTracker isAlwaysRender={true} className={'d-flex flex-fill'}>
        {(width, height) => {
          return (
            <ScrollComponent
              theadHeight={this.getTheadHeight()}
              appendStyle={{ maxHeight: height, minHeight: 0 }}
              appendHandleScroll={onScrollFunction}
              scrollRef={scrollRef}
            >
              {this.renderContentScroll()}
            </ScrollComponent>
          );
        }}
      </SizeTracker>
    );
  };

  render() {
    const { multiHead, isLoading } = this.props;
    const rootStyle = isLoading
      ? {
          userSelect: 'none',
          pointerEvents: 'none',
          minHeight: ConstCommon.MIN_HEIGHT_SCROLL,
        }
      : {
          minHeight: ConstCommon.MIN_HEIGHT_SCROLL,
        };

    return (
      <div
        style={rootStyle}
        className="data-table-wrap d-flex flex-fill flex-column position-relative"
      >
        {isLoading && <Loading />}
        {multiHead && (
          <div className="fs-14 table-header">
            <strong>{multiHead.name}</strong>
          </div>
        )}
        {this.renderScroll()}
      </div>
    );
  }
}

Table.propTypes = {
  ids: PropTypes.array,
  schema: PropTypes.array,
  columnDraggable: PropTypes.bool,
  rowDraggable: PropTypes.bool,
  resizeable: PropTypes.bool,
  hideColumns: PropTypes.array,
  getDataFromRedux: PropTypes.func.isRequired,
  onSort: PropTypes.func,
  onScrollFunction: PropTypes.func,
  groups: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  stickyFirstColumn: PropTypes.bool,
  stickyHead: PropTypes.bool,
  isLoading: PropTypes.bool,
  clickRow: PropTypes.func,
  thValign: PropTypes.string,
  noDataText: PropTypes.string,
  scrollRef: PropTypes.object,
  changeIdHover: PropTypes.func,
  tdPadding: PropTypes.number,
  sortState: PropTypes.object,
  onRowReorder: PropTypes.func,
  onToggleRow: PropTypes.func,
};

Table.defaultProps = {
  ids: [],
  schema: [],
  hideColumns: [],
  columnDraggable: false,
  rowDraggable: false,
  resizeable: true,
  onReorderRow: () => {},
  onSort: () => {},
  onScrollFunction: () => {},
  groups: false,
  stickyFirstColumn: true,
  stickyHead: true,
  onResize: () => {},
  isLoading: false,
  clickRow: id => {},
  thValign: 'center',
  noDataText: '',
  scrollRef: React.createRef(),
  changeIdHover: () => {},
  tdPadding: 3,
  sortState: {},
  onRowReorder: () => {},
  onToggleRow: () => {},
};

const mapStateToProps = (state, { getDataFromRedux }) => ({
  data: getDataFromRedux(state),
  fontSize: state.fontSize,
});

export default connect(
  mapStateToProps,
  {},
  null,
  { forwardRef: true },
)(Table);
